import {
  closestCenter,
  DndContext,
  DragEndEvent,
  MouseSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Switch } from "@mui/material";
import {
  useEffect,
  useMemo,
  useState,
  type ChangeEvent,
  type CSSProperties,
  type FC,
} from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import styled from "styled-components";

import { PIC } from "@ryde/assets";
import {
  getGridLabelKey,
  Log,
  type CustomFieldDto,
  type IStyledAttributes,
  type RydeUiUnit,
  type VisibilityState,
} from "@ryde/shared";
import { deepPurpleMui, rydeColors } from "@ryde/theme";

import { FramerTooltip } from "../../../common";
import { HR } from "../../html-tags";
import { ColumnItem, ColumnItemText } from "./column.draggable.styled";

export const ColumnsOrderDraggableContent = styled.div.attrs<IStyledAttributes>(
  {
    "data-e2e": "column-order-draggable-content",
  },
)`
  width: 100%;
  color: ${rydeColors.black};
  padding: 20px 20px 20px;
  label {
    display: block;
  }
  .idLabel {
    color: ${deepPurpleMui._300};
    margin-bottom: 20px;
  }
`;

interface IColumnDraggableProps {
  item: CustomFieldDto;
  currentVisibility: "on" | "off";
  onVisibilityChange: (itemId: number, checked: boolean) => void;
  entity: RydeUiUnit;
}

const ColumnDraggableSortable: FC<IColumnDraggableProps> = ({
  item,
  currentVisibility,
  onVisibilityChange,
  entity,
}) => {
  const { t } = useTranslation();
  const onSwitchChange = useEvent(
    (_event: ChangeEvent<HTMLInputElement>, checked: boolean): void => {
      onVisibilityChange(item.id, checked);
    },
  );
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `draggable_${item.id}`,
    disabled: item.gridOrderable === false,
  });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition: transition || undefined,
  };

  return (
    <ColumnItem
      ref={setNodeRef}
      style={style}
      {...attributes}
      $isDragging={isDragging}
    >
      <PIC.SixDotsSvg className="pointer" {...listeners} />
      <Switch
        data-e2e-id={`column_${item.id}`}
        checked={currentVisibility === "on"}
        disabled={item.gridOrderable === false}
        onChange={onSwitchChange}
      />
      <ColumnItemText>
        <FramerTooltip content={item.name}>
          <span>
            {t(getGridLabelKey(entity, item.name), {
              defaultValue: item.name,
            })}
          </span>
        </FramerTooltip>
      </ColumnItemText>
    </ColumnItem>
  );
};

const ColumnInactiveRow: FC<IColumnDraggableProps> = ({
  item,
  currentVisibility,
  onVisibilityChange,
  entity,
}) => {
  const { t } = useTranslation();
  const onSwitchChange = useEvent(
    (_event: ChangeEvent<HTMLInputElement>, checked: boolean): void => {
      onVisibilityChange(item.id, checked);
    },
  );

  return (
    <ColumnItem>
      <PIC.SixDotsSvg className="pointer inactive" />
      <Switch
        data-e2e-id={`column_${item.id}`}
        checked={currentVisibility === "on"}
        disabled={item.gridOrderable === false}
        onChange={onSwitchChange}
      />
      <ColumnItemText>
        <FramerTooltip content={item.name}>
          <span>
            {t(getGridLabelKey(entity, item.name), {
              defaultValue: item.name,
            })}
          </span>
        </FramerTooltip>
      </ColumnItemText>
    </ColumnItem>
  );
};

export interface IColumnsOrderDraggableProps {
  columnOrder: number[];
  setColumnOrder?: (columns: number[]) => void;
  columnVisibility?: VisibilityState;
  setColumnVisibility?: (itemId: number, isVisible: boolean) => void;
  tableSettingsDrawerClose?: () => void;
  itemsAll: CustomFieldDto[];
  entity: RydeUiUnit;
}

export const ColumnsOrderDraggable: FC<IColumnsOrderDraggableProps> = (
  props: IColumnsOrderDraggableProps,
) => {
  const {
    columnOrder,
    columnVisibility,
    setColumnOrder,
    setColumnVisibility,
    itemsAll,
    entity,
  } = props;
  const { t } = useTranslation();
  const { watch } = useFormContext();
  const pickedColumns = watch("columns");

  const [activeItems, setActiveItems] = useState<CustomFieldDto[]>();
  const [inactiveItems, setInactiveItems] = useState<CustomFieldDto[]>();

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
  );

  const activeSortableIds = useMemo(
    () => activeItems?.map(item => `draggable_${item.id}`) ?? [],
    [activeItems],
  );

  useEffect(() => {
    const pickedColumnsSet = pickedColumns ? new Set(pickedColumns) : null;
    const items = itemsAll.filter(item => pickedColumnsSet?.has(item.id)) || [];
    const itemsSorted = columnOrder
      ? items.sort(
          (a, b) => columnOrder.indexOf(a.id) - columnOrder.indexOf(b.id),
        )
      : items;
    const active =
      itemsSorted.filter(
        item =>
          columnVisibility?.[item.id] !== undefined &&
          columnVisibility?.[item.id] !== false,
      ) || [];
    setActiveItems(active);
    setInactiveItems(
      itemsSorted.filter(
        item =>
          columnVisibility?.[item.id] === undefined ||
          columnVisibility?.[item.id] === false,
      ) || [],
    );
  }, [pickedColumns, columnVisibility, columnOrder, itemsAll]);

  // useEvent closures read render values through an object (see conventions.md → useEvent).
  const itemsContext = { activeItems, inactiveItems };

  const onDragEnd = useEvent((event: DragEndEvent): void => {
    const { active, over } = event;
    const { activeItems: items } = itemsContext;

    if (!over || active.id === over.id || !items) {
      return;
    }

    const oldIndex = items.findIndex(
      item => `draggable_${item.id}` === String(active.id),
    );
    const newIndex = items.findIndex(
      item => `draggable_${item.id}` === String(over.id),
    );

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    if (!items[oldIndex].gridOrderable) {
      Log.warnAndToast(t("errors.COLUMN_IS_NOT_ORDERABLE"));
      return;
    }

    const reordered = arrayMove(items, oldIndex, newIndex);
    const ordered = reordered.map(el => el.id);

    if (ordered.length && setColumnOrder) {
      setColumnOrder(ordered);
    }

    setActiveItems(reordered);
  });

  const onVisibilityChange = useEvent(
    (itemId: number, checked: boolean): void => {
      const { activeItems: active, inactiveItems: inactive } = itemsContext;

      if (checked) {
        const newActive = inactive?.find(x => x.id === itemId);

        if (active && newActive) {
          setActiveItems(items => [...(items as CustomFieldDto[]), newActive]);
          setInactiveItems(items => items?.filter(x => x.id !== itemId));
          setColumnVisibility?.(itemId, true);
        }
      } else {
        const newInactive = active?.find(x => x.id === itemId);

        if (inactive && newInactive) {
          setColumnVisibility?.(itemId, false);
          setInactiveItems(items => [
            ...(items as CustomFieldDto[]),
            newInactive,
          ]);
          setActiveItems(items => items?.filter(x => x.id !== itemId));
        }
      }
    },
  );

  return (
    <ColumnsOrderDraggableContent>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={onDragEnd}
      >
        <SortableContext
          items={activeSortableIds}
          strategy={verticalListSortingStrategy}
        >
          {activeItems?.map(row => (
            <ColumnDraggableSortable
              entity={entity}
              item={row}
              key={row.id}
              currentVisibility={"on"}
              onVisibilityChange={onVisibilityChange}
            />
          ))}
        </SortableContext>
      </DndContext>

      <HR />

      {inactiveItems?.map(row => (
        <ColumnInactiveRow
          entity={entity}
          item={row}
          key={row.id}
          currentVisibility={"off"}
          onVisibilityChange={onVisibilityChange}
        />
      ))}
    </ColumnsOrderDraggableContent>
  );
};
